import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';                 // ajouté
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import GlobalLayout from '../GlobalLayout';
import { getQuery, updateQuery, createQuery, getApps } from '../../services/admin_api';  // ajout getApps
import { getRawData } from '../../services/front_api';

// 1) Import du core ACE
import 'ace-builds/src-noconflict/ace';
// 2) Import des modes dont dépend votre mode personnalisé
import 'ace-builds/src-noconflict/mode-pgsql';
import 'ace-builds/src-noconflict/mode-twig';
// 3) Import de votre mode combiné
import 'ace-builds/src-noconflict/mode-pgsql_twig';
// 4) Import du thème
import 'ace-builds/src-noconflict/theme-monokai';

import AceEditor from 'react-ace';

const stringifyApiError = (value) => {
    if (!value) return '';

    if (typeof value === 'string') {
        return value;
    }

    if (Array.isArray(value)) {
        return value.map(stringifyApiError).filter(Boolean).join(' | ');
    }

    if (typeof value === 'object') {
        if (value.message) return stringifyApiError(value.message);
        if (value.error) return stringifyApiError(value.error);
        if (value.errors) return stringifyApiError(value.errors);

        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }

    return String(value);
};

const getApiErrorMessage = (error) => {
    const responseData = error?.response?.data;
    const status = error?.response?.status ? `HTTP ${error.response.status} - ` : '';
    return `${status}${stringifyApiError(responseData) || error?.message || 'Erreur inconnue'}`;
};

const getChartResponseError = (response) => {
    if (response?.isAxiosError || response?.response) {
        return getApiErrorMessage(response);
    }

    if (response?.error || response?.errors) {
        return stringifyApiError(response.error || response.errors);
    }

    if (response?.message && !response?.data) {
        return stringifyApiError(response.message);
    }

    return '';
};

const PREVIEWABLE_QUERY_CATEGORIES = ['chart', 'indic'];

const QueryEdit = () => {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();

    const [apps, setApps] = useState([]);
    const [queryData, setQueryData] = useState({
        name: '',
        template: '',
        app_id: '',
        category: '',
        label: '',
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [validatingChart, setValidatingChart] = useState(false);
    const [chartValidation, setChartValidation] = useState(null);

    // Récupère la liste des apps pour le select
    useEffect(() => {
        const fetchApps = async () => {
            try {
                const data = await getApps();
                setApps(data);
            } catch (err) {
                console.error('Erreur lors de la récupération des apps', err);
            }
        };
        fetchApps();
    }, []);

    // Charge la query si on édite
    useEffect(() => {
        if (!isEdit) {
            setLoading(false);
            return;
        }
        const fetchQuery = async () => {
            try {
                const data = await getQuery(id);
                setQueryData({
                    name: data.name,
                    template: data.template,
                    app_id: data.app_id,
                    category: data.category,
                    label: data.label,
                });
                setError('');
            } catch {
                setError('Erreur lors du chargement de la Query.');
            } finally {
                setLoading(false);
            }
        };
        fetchQuery();
    }, [id, isEdit]);

    const handleChange = (event) => {
        setChartValidation(null);
        setQueryData({
            ...queryData,
            [event.target.name]: event.target.value,
        });
    };

    const handleTemplateChange = (newValue) => {
        setChartValidation(null);
        setQueryData({
            ...queryData,
            template: newValue,
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            if (isEdit) {
                await updateQuery(id, queryData);
                alert('Query mise à jour avec succès.');
            } else {
                await createQuery(queryData);
                alert('Query créée avec succès.');
            }
            navigate('/query');
        } catch {
            setError(`Erreur lors de la ${isEdit ? 'mise à jour' : 'création'} de la Query.`);
        }
    };

    const handleValidateChart = async () => {
        if (validatingChart) return;

        const queryCategory = queryData.category;

        if (!isEdit) {
            setChartValidation({
                severity: 'warning',
                message: 'Créez la Query avant de lancer une validation.',
            });
            return;
        }

        if (!PREVIEWABLE_QUERY_CATEGORIES.includes(queryCategory)) {
            setChartValidation({
                severity: 'warning',
                message: 'La validation est disponible uniquement pour les Queries de catégorie chart ou indic.',
            });
            return;
        }

        if (!queryData.app_id || !queryData.name.trim()) {
            setChartValidation({
                severity: 'warning',
                message: 'Renseignez au minimum une App et un Nom avant de lancer la validation.',
            });
            return;
        }

        setValidatingChart(true);
        setChartValidation(null);

        try {
            await updateQuery(id, queryData);
            const [response, url] = await getRawData(queryData.app_id, queryData.name.trim(), queryCategory);
            const chartError = getChartResponseError(response);

            if (chartError) {
                setChartValidation({
                    severity: 'error',
                    message: `Erreur retournée par l'API ${queryCategory} : ${chartError}`,
                });
                return;
            }

            setChartValidation({
                severity: 'success',
                message: `Validation OK : l'appel ${queryCategory} ne retourne pas d'erreur (${url}).`,
            });
        } catch (err) {
            setChartValidation({
                severity: 'error',
                message: `Erreur lors de la validation ${queryCategory} : ${getApiErrorMessage(err)}`,
            });
        } finally {
            setValidatingChart(false);
        }
    };

    if (loading) return <div>Chargement...</div>;

    return (
        <GlobalLayout>
            <div style={{ padding: '1rem', margin: '0 auto' }}>
                <h1>{isEdit ? 'Éditer la Query' : 'Nouvelle Query'}</h1>
                {error && <p style={{ color: 'red' }}>{error}</p>}
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <FormControl fullWidth>
                                <InputLabel id="app-select-label">App</InputLabel>
                                <Select
                                    labelId="app-select-label"
                                    name="app_id"
                                    value={queryData.app_id}
                                    label="App"
                                    onChange={handleChange}
                                >
                                    <MenuItem value="">
                                        <em>Aucun</em>
                                    </MenuItem>
                                    {apps.map((app) => (
                                        <MenuItem key={app.id} value={app.id}>
                                            {app.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <FormControl fullWidth>
                                <InputLabel id="category-select-label">Category</InputLabel>
                                <Select
                                    labelId="category-select-label"
                                    name="category"
                                    value={queryData.category}
                                    label="Category"
                                    onChange={handleChange}
                                >
                                    <MenuItem value="">
                                        <em>Aucun</em>
                                    </MenuItem>
                                    <MenuItem value="indic">indic</MenuItem>
                                    <MenuItem value="chart">chart</MenuItem>
                                    <MenuItem value="grid">grid</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                label="Nom"
                                name="name"
                                value={queryData.name}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12 }}>
                            <TextField
                                label="Label"
                                name="label"
                                value={queryData.label}
                                onChange={handleChange}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }}>
                            <InputLabel style={{ marginBottom: 8 }}>Template</InputLabel>
                            <AceEditor
                                mode="pgsql_twig"
                                theme="monokai"
                                name="ace-editor"
                                onChange={handleTemplateChange}
                                value={queryData.template}
                                width="100%"
                                height="600px"
                                setOptions={{ useWorker: false }}
                            />
                        </Grid>
                        <Grid size={12}>
                            <Button variant="contained" color="primary" type="submit">
                                {isEdit ? 'Enregistrer' : 'Créer'}
                            </Button>
                            {isEdit && PREVIEWABLE_QUERY_CATEGORIES.includes(queryData.category) && (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    type="button"
                                    onClick={handleValidateChart}
                                    disabled={validatingChart}
                                    style={{ marginLeft: '1rem' }}
                                >
                                    {validatingChart && (
                                        <CircularProgress
                                            size={16}
                                            color="inherit"
                                            style={{ marginRight: 8 }}
                                        />
                                    )}
                                    Enregistrer et valider
                                </Button>
                            )}
                            {isEdit && (
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to={`/query/clauses/${queryData.app_id}/${queryData.category}`}
                                    style={{ marginLeft: '1rem' }}
                                >
                                    Editer les clauses
                                </Button>
                            )}
                            {isEdit && queryData.category === 'grid' && (
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to={`/query/coldef/${id}`}
                                    style={{ marginLeft: '1rem' }}
                                >
                                    Editer la Coldef
                                </Button>
                            )}
                        </Grid>
                        {chartValidation && (
                            <Grid size={12}>
                                <Alert severity={chartValidation.severity}>
                                    {chartValidation.message}
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </form>
            </div>
        </GlobalLayout>
    );
};

export default QueryEdit;
